import { useCallback, useEffect, useState } from "react";
import { Building2, Eye, EyeOff, RefreshCw, Save } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { HOSPITAL_PROFILE_CONFIG_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

const TYPE_LABEL = {
    text: "Text", textarea: "Notes", number: "Number",
    boolean: "Yes / No", choice: "Pick one", multi: "Pick many",
};

/**
 * Which hospital-profile fields this tenant asks its sales team for.
 *
 * Switching one off stops it being collected; it does not erase or hide what
 * has already been recorded. The count next to each field is how many leads
 * would keep showing it, so the consequence is visible before the switch flips.
 */
export default function HospitalProfileConfig() {
    const { fetch, update } = useApiMethods();

    const [groups, setGroups]     = useState([]);
    const [enabled, setEnabled]   = useState({});
    const [leadCount, setLeads]   = useState(0);
    const [canEdit, setCanEdit]   = useState(false);
    const [loading, setLoading]   = useState(false);
    const [saving, setSaving]     = useState(false);
    const [dirty, setDirty]       = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        fetch(HOSPITAL_PROFILE_CONFIG_URL)
            .then(res => {
                if (res?.status === "success") {
                    setGroups(res.groups || []);
                    setLeads(res.lead_count || 0);
                    setCanEdit(!!res.can_edit);
                    const map = {};
                    (res.groups || []).forEach(g =>
                        (g.fields || []).forEach(f => { map[f.key] = f.is_enabled; }));
                    setEnabled(map);
                    setDirty(false);
                } else {
                    ToastMsg("error", res?.message || "Failed to load fields");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load fields"))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = (key) => {
        if (!canEdit) return;
        setEnabled(e => ({ ...e, [key]: !e[key] }));
        setDirty(true);
    };

    const save = () => {
        setSaving(true);
        update(HOSPITAL_PROFILE_CONFIG_URL, { fields: enabled })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", "Field settings saved");
                    setDirty(false);
                } else ToastMsg("error", res?.message || "Could not save");
            })
            .catch(() => ToastMsg("error", "Could not save"))
            .finally(() => setSaving(false));
    };

    const onCount = Object.values(enabled).filter(Boolean).length;
    const total   = Object.keys(enabled).length;

    return (
        <ContainerCard>
            <div className="p-4 max-w-3xl">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-indigo-500" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Hospital Profile
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                What the sales team is asked for on the Hospital Profile tab.
                                {" "}{onCount} of {total} fields on · {leadCount} lead{leadCount === 1 ? "" : "s"} profiled
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={load} title="Reload"
                            className="h-8 w-8 rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center">
                            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
                        </button>
                        {canEdit && (
                            <button onClick={save} disabled={!dirty || saving}
                                className="h-8 px-3 rounded-md text-xs font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white flex items-center gap-1.5">
                                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
                            </button>
                        )}
                    </div>
                </div>

                {!canEdit && (
                    <p className="mb-4 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                        Only a super admin can change these. You are seeing the current settings.
                    </p>
                )}

                <div className="space-y-4">
                    {groups.map(group => (
                        <div key={group.group}
                            className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/40 border-b border-gray-100 dark:border-gray-700">
                                {group.group}
                            </p>
                            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                {(group.fields || []).map(f => {
                                    const on = !!enabled[f.key];
                                    return (
                                        <li key={f.key} className="flex items-center gap-3 px-3 py-2">
                                            <button
                                                onClick={() => toggle(f.key)}
                                                disabled={!canEdit}
                                                title={on ? "Collected" : "Not collected"}
                                                className={cn(
                                                    "h-6 w-6 rounded-md border flex items-center justify-center flex-shrink-0 transition",
                                                    on
                                                        ? "border-indigo-200 bg-indigo-50 text-indigo-600 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                                                        : "border-gray-200 text-gray-400 dark:border-gray-600",
                                                    canEdit ? "hover:bg-gray-100 dark:hover:bg-gray-700" : "cursor-default opacity-70"
                                                )}>
                                                {on ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                                            </button>

                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className={cn(
                                                        "text-xs font-medium",
                                                        on ? "text-gray-800 dark:text-gray-200"
                                                           : "text-gray-400 dark:text-gray-500 line-through"
                                                    )}>
                                                        {f.label}
                                                    </span>
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                        {TYPE_LABEL[f.type] || f.type}
                                                    </span>
                                                    {f.unit && (
                                                        <span className="text-[9px] text-gray-400">in {f.unit}</span>
                                                    )}
                                                </div>
                                                {f.help_text && (
                                                    <p className="mt-0.5 text-[10px] text-gray-400 italic">{f.help_text}</p>
                                                )}
                                            </div>

                                            {/* Says what switching off would NOT do. */}
                                            {f.answered_count > 0 && (
                                                <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0"
                                                    title="These leads keep showing this field even when it is switched off">
                                                    {f.answered_count} answered
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </ContainerCard>
    );
}
